import { Link } from 'react-router-dom';

/**
 * A plan allowance, and what is left of it.
 *
 * The limits were always in the subscription summary; the consumption was
 * not, so the only way for an owner to find out how close they were to a cap
 * was to try to cross it and be refused. Counted the way the server guard
 * counts - every property, and only active users - so the screen and the
 * guard cannot disagree about whether the next one fits.
 */
export interface Allowance {
  used: number;
  limit: number;
  /** Clamped at zero: a plan downgrade can leave a hotel above its cap. */
  left: number;
  /** True when the next one would be refused. */
  full: boolean;
  /** True when they are already past it, which only a downgrade can do. */
  over: boolean;
}

export function allowance(used: number, limit: number): Allowance {
  return {
    used,
    limit,
    left: Math.max(0, limit - used),
    full: used >= limit,
    over: used > limit,
  };
}

/** "property" / "properties" / "users", for a sentence about a cap. */
const noun = (what: 'properties' | 'users', count: number) =>
  what === 'users' ? 'users' : count === 1 ? 'property' : 'properties';

/**
 * A used/allowed line with a bar under it.
 *
 * Amber from four fifths of the way along and red at the cap, so the hotel
 * finds out it is running out of room before the refusal rather than from it.
 */
export function AllowanceMeter({
  label,
  what,
  allowance: a,
}: {
  label: string;
  what: 'properties' | 'users';
  allowance: Allowance;
}) {
  const share = a.limit > 0 ? Math.min(100, Math.round((a.used / a.limit) * 100)) : 100;
  const tone = a.full ? ' danger' : share >= 80 ? ' warn' : '';
  return (
    <div className="allowance">
      <div className="stat-line">
        <span className="dim">{label}</span>
        <b>
          {a.used} of {a.limit}
        </b>
      </div>
      <div
        className={`meter${tone}`}
        role="img"
        aria-label={`${a.used} of ${a.limit} ${noun(what, a.limit)} used`}
      >
        <span style={{ width: `${share}%` }} />
      </div>
      <div className="xs muted">
        {a.over
          ? `${a.used - a.limit} over the plan allowance`
          : a.full
            ? 'Plan allowance reached'
            : `${a.left} left on this plan`}
      </div>
    </div>
  );
}

/**
 * Says what the server would say, before they have spent a form filling it in.
 *
 * Deliberately the same sentence the API refuses with - an owner who hits
 * both should read one explanation, not two that sound like different
 * problems. `canBill` is false for anyone who cannot see the billing page, so
 * they get the fact without a link that would only refuse them.
 */
export function AllowanceNotice({
  planName,
  what,
  allowance: a,
  canBill,
}: {
  planName: string;
  what: 'properties' | 'users';
  allowance: Allowance;
  canBill: boolean;
}) {
  return (
    <div className="form-note warn">
      The {planName} plan covers {a.limit} {noun(what, a.limit)}, and you are using {a.used}.{' '}
      {canBill ? (
        <>
          <Link to="/billing">Upgrade your plan</Link> to add another.
        </>
      ) : (
        'Ask the owner to upgrade the plan to add another.'
      )}
    </div>
  );
}
